import { Field, Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import clsx from 'clsx';
import { Upload, Wifi } from 'lucide-react';

import {
  isBE,
  isLimitedToBE,
} from '@/react/portainer/feature-flags/feature-flags.service';
import { success as notifySuccess } from '@/portainer/services/notifications';
import { FeatureId } from '@/react/portainer/feature-flags/enums';
import {
  readFileAsArrayBuffer,
  arrayBufferToBase64,
} from '@/portainer/services/fileUploadReact';

import { FormControl } from '@@/form-components/FormControl';
import { LoadingButton } from '@@/buttons/LoadingButton';
import { Input, Select } from '@@/form-components/Input';
import { SwitchField } from '@@/form-components/SwitchField';
import { FormSection } from '@@/form-components/FormSection';
import { FileUploadField } from '@@/form-components/FileUpload/FileUploadField';

import { useBackupAzureSettings } from './queries/useBackupAzureSettings';
import { useExportAzureBackupMutation } from './queries/useExportAzureBackupMutation';
import { useTestAzureConnectionMutation } from './queries/useTestAzureConnectionMutation';
import { useUpdateBackupAzureSettingsMutation } from './queries/useUpdateBackupAzureSettingsMutation';
import {
  AzureAuthMethod,
  BackupAzureModel,
  BackupAzureSettings,
} from './types';
import { validationSchema } from './BackupAzureForm.validation';
import { SecurityFieldset } from './SecurityFieldset';

type AzureCredentials = Pick<
  BackupAzureModel,
  | 'tenantId'
  | 'clientId'
  | 'clientSecret'
  | 'clientCertificate'
  | 'clientCertificatePassword'
  | 'managedIdentityClientId'
  | 'storageAccountKey'
>;

const noCredentials: AzureCredentials = {
  tenantId: '',
  clientId: '',
  clientSecret: '',
  clientCertificate: '',
  clientCertificatePassword: '',
  managedIdentityClientId: '',
  storageAccountKey: '',
};

const authMethodOptions: ReadonlyArray<{
  label: string;
  value: AzureAuthMethod;
}> = [
  { label: 'Service Principal (Secret)', value: 'servicePrincipalSecret' },
  {
    label: 'Service Principal (Certificate)',
    value: 'servicePrincipalCertificate',
  },
  { label: 'Managed Identity', value: 'managedIdentity' },
  { label: 'Storage Account Key', value: 'storageAccountKey' },
];

export function BackupAzureForm({ onSave }: { onSave?: () => void }) {
  const limitedToBE = isLimitedToBE(FeatureId.AZURE_BLOB_BACKUP_SETTING);
  const [certFile, setCertFile] = useState<File | null>(null);

  const exportMutate = useExportAzureBackupMutation();
  const testMutate = useTestAzureConnectionMutation();
  const updateMutate = useUpdateBackupAzureSettingsMutation();

  const settingsQuery = useBackupAzureSettings({ enabled: isBE });
  if (settingsQuery.isLoading) {
    return null;
  }

  const settings = settingsQuery.data;

  const initialValues: BackupAzureSettings = {
    scheduleAutomaticBackup: !!settings?.cronRule,
    cronRule: settings?.cronRule || '',
    storageAccountName: settings?.storageAccountName || '',
    containerName: settings?.containerName || '',
    authMethod: settings?.authMethod || 'servicePrincipalSecret',
    tenantId: settings?.tenantId || '',
    clientId: settings?.clientId || '',
    clientSecret: settings?.clientSecret || '',
    clientCertificate: settings?.clientCertificate || '',
    clientCertificatePassword: settings?.clientCertificatePassword || '',
    managedIdentityClientId: settings?.managedIdentityClientId || '',
    storageAccountKey: settings?.storageAccountKey || '',
    serviceUrl: settings?.serviceUrl || '',
    passwordProtect: !!settings?.password,
    password: settings?.password || '',
  };

  return (
    <Formik<BackupAzureSettings>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      validateOnMount
    >
      {({ values, errors, isSubmitting, setFieldValue, isValid }) => (
        <Form className="form-horizontal">
          <div className="form-group">
            <div className="col-sm-12">
              <SwitchField
                name="azure-schedule-automatic-backup"
                data-cy="azure-backup-scheduleAutomaticBackupSwitch"
                labelClass="col-sm-3 col-lg-2"
                label="Schedule automatic backups"
                checked={values.scheduleAutomaticBackup}
                featureId={FeatureId.AZURE_BLOB_BACKUP_SETTING}
                onChange={(e) => setFieldValue('scheduleAutomaticBackup', e)}
              />
            </div>
          </div>

          {values.scheduleAutomaticBackup && (
            <FormControl
              inputId="azure_cron_rule"
              label="Cron rule"
              size="small"
              errors={errors.cronRule}
              required
            >
              <Field
                id="azure_cron_rule"
                name="cronRule"
                type="text"
                as={Input}
                placeholder="0 2 * * *"
                data-cy="azure-backup-cronRuleInput"
                className={clsx({ 'limited-be': limitedToBE })}
                disabled={limitedToBE}
              />
            </FormControl>
          )}

          <FormControl
            label="Storage account name"
            inputId="azure_storage_account_name"
            errors={errors.storageAccountName}
            required
          >
            <Field
              id="azure_storage_account_name"
              name="storageAccountName"
              type="text"
              as={Input}
              data-cy="azure-backup-storageAccountNameInput"
              className={clsx({ 'limited-be': limitedToBE })}
              disabled={limitedToBE}
            />
          </FormControl>

          <FormControl
            label="Container name"
            inputId="azure_container_name"
            errors={errors.containerName}
            required
          >
            <Field
              id="azure_container_name"
              name="containerName"
              type="text"
              as={Input}
              data-cy="azure-backup-containerNameInput"
              className={clsx({ 'limited-be': limitedToBE })}
              disabled={limitedToBE}
            />
          </FormControl>

          <FormControl
            label="Authentication method"
            inputId="azure_auth_method"
            errors={errors.authMethod}
          >
            <Select<AzureAuthMethod>
              id="azure_auth_method"
              value={values.authMethod}
              onChange={(e) =>
                setFieldValue('authMethod', e.currentTarget.value)
              }
              options={authMethodOptions}
              disabled={limitedToBE}
              data-cy="azure-backup-authMethodSelect"
            />
          </FormControl>

          {(values.authMethod === 'servicePrincipalSecret' ||
            values.authMethod === 'servicePrincipalCertificate') && (
            <>
              <FormControl
                label="Tenant ID"
                inputId="azure_tenant_id"
                errors={errors.tenantId}
                required
              >
                <Field
                  id="azure_tenant_id"
                  name="tenantId"
                  type="text"
                  as={Input}
                  data-cy="azure-backup-tenantIdInput"
                  className={clsx({ 'limited-be': limitedToBE })}
                  disabled={limitedToBE}
                />
              </FormControl>

              <FormControl
                label="Client ID"
                inputId="azure_client_id"
                errors={errors.clientId}
                required
              >
                <Field
                  id="azure_client_id"
                  name="clientId"
                  type="text"
                  as={Input}
                  data-cy="azure-backup-clientIdInput"
                  className={clsx({ 'limited-be': limitedToBE })}
                  disabled={limitedToBE}
                />
              </FormControl>
            </>
          )}

          {values.authMethod === 'servicePrincipalSecret' && (
            <FormControl
              label="Client secret"
              inputId="azure_client_secret"
              errors={errors.clientSecret}
              required
            >
              <Field
                id="azure_client_secret"
                name="clientSecret"
                type="password"
                as={Input}
                data-cy="azure-backup-clientSecretInput"
                className={clsx({ 'limited-be': limitedToBE })}
                disabled={limitedToBE}
              />
            </FormControl>
          )}

          {values.authMethod === 'servicePrincipalCertificate' && (
            <>
              <FormControl
                label="Client certificate (PFX)"
                inputId="azure_client_certificate"
                tooltip="PFX (PKCS#12) file containing the private key and certificate. Generate with: az ad sp create-for-rbac --create-cert, or convert with: openssl pkcs12 -export -out cert.pfx -inkey key.pem -in cert.crt"
                errors={errors.clientCertificate}
                required
              >
                <FileUploadField
                  inputId="azure_client_certificate"
                  title="Select PFX file"
                  accept=".pfx"
                  value={certFile}
                  onChange={(file) =>
                    handleCertificateUpload(file, setCertFile, setFieldValue)
                  }
                  state={
                    values.clientCertificate && !certFile
                      ? 'success'
                      : undefined
                  }
                  data-cy="azure-backup-clientCertificateInput"
                  disabled={limitedToBE}
                />
              </FormControl>

              <FormControl
                label="Certificate password"
                inputId="azure_certificate_password"
                errors={errors.clientCertificatePassword}
              >
                <Field
                  id="azure_certificate_password"
                  name="clientCertificatePassword"
                  type="password"
                  as={Input}
                  placeholder="Leave blank if the certificate has no password"
                  data-cy="azure-backup-clientCertificatePasswordInput"
                  className={clsx({ 'limited-be': limitedToBE })}
                  disabled={limitedToBE}
                />
              </FormControl>
            </>
          )}

          {values.authMethod === 'managedIdentity' && (
            <FormControl
              label="Managed identity client ID"
              inputId="azure_mi_client_id"
              tooltip="Client ID of a user-assigned managed identity. Leave blank to use the system-assigned identity"
              errors={errors.managedIdentityClientId}
            >
              <Field
                id="azure_mi_client_id"
                name="managedIdentityClientId"
                type="text"
                as={Input}
                placeholder="Leave blank for system-assigned identity"
                data-cy="azure-backup-managedIdentityClientIdInput"
                className={clsx({ 'limited-be': limitedToBE })}
                disabled={limitedToBE}
              />
            </FormControl>
          )}

          {values.authMethod === 'storageAccountKey' && (
            <FormControl
              label="Storage account key"
              inputId="azure_storage_account_key"
              tooltip="One of the two access keys from the Azure Portal Storage Account Keys blade"
              errors={errors.storageAccountKey}
              required
            >
              <Field
                id="azure_storage_account_key"
                name="storageAccountKey"
                type="password"
                as={Input}
                data-cy="azure-backup-storageAccountKeyInput"
                className={clsx({ 'limited-be': limitedToBE })}
                disabled={limitedToBE}
              />
            </FormControl>
          )}

          <FormSection title="Advanced">
            <FormControl
              label="Service URL"
              inputId="azure_service_url"
              tooltip="Custom Azure Blob Storage endpoint. Leave blank to use the standard public endpoint"
              errors={errors.serviceUrl}
            >
              <Field
                id="azure_service_url"
                name="serviceUrl"
                type="text"
                as={Input}
                placeholder="Leave blank for public Azure"
                data-cy="azure-backup-serviceUrlInput"
                className={clsx({ 'limited-be': limitedToBE })}
                disabled={limitedToBE}
              />
            </FormControl>
          </FormSection>

          <SecurityFieldset
            switchDataCy="azure-backup-passwordProtectToggle"
            inputDataCy="azure-backup-passwordInput"
            disabled={limitedToBE}
          />

          <div className="form-group">
            <div className="col-sm-12 flex gap-2">
              <LoadingButton
                type="button"
                loadingText="Backing up..."
                isLoading={exportMutate.isLoading}
                className={clsx('!ml-0', { 'limited-be': limitedToBE })}
                disabled={!isValid || limitedToBE || exportMutate.isLoading}
                data-cy="azure-backup-exportButton"
                icon={Upload}
                onClick={() => handleExport(values)}
              >
                Back up now
              </LoadingButton>

              <LoadingButton
                type="button"
                loadingText="Testing..."
                isLoading={testMutate.isLoading}
                className={clsx('!ml-0', { 'limited-be': limitedToBE })}
                disabled={!isValid || limitedToBE || testMutate.isLoading}
                data-cy="azure-backup-testConnectionButton"
                icon={Wifi}
                color="default"
                onClick={() => handleTest(values)}
              >
                Test connection
              </LoadingButton>
            </div>
          </div>

          <div className="form-group">
            <hr />
            <div className="col-sm-12">
              <LoadingButton
                loadingText="Saving settings..."
                isLoading={isSubmitting}
                className={clsx('!ml-0', { 'limited-be': limitedToBE })}
                disabled={!isValid || limitedToBE}
                data-cy="azure-backup-saveSettingsButton"
              >
                Save backup settings
              </LoadingButton>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );

  async function handleCertificateUpload(
    file: File,
    setFile: (f: File) => void,
    setFieldValue: (field: string, value: string) => void
  ) {
    setFile(file);
    const buffer = await readFileAsArrayBuffer(file);
    if (buffer) {
      setFieldValue('clientCertificate', arrayBufferToBase64(buffer));
      // The stored password belongs to the certificate being replaced, so it
      // must be re-entered rather than saved against the new one.
      setFieldValue('clientCertificatePassword', '');
    }
  }

  function buildPayload(values: BackupAzureSettings): BackupAzureModel {
    return {
      cronRule: values.scheduleAutomaticBackup ? values.cronRule : '',
      storageAccountName: values.storageAccountName,
      containerName: values.containerName,
      authMethod: values.authMethod,
      serviceUrl: values.serviceUrl,
      password: values.passwordProtect ? values.password : '',
      ...credentialsForAuthMethod(values),
    };
  }

  // Only the credentials of the selected auth method are sent. The rest are
  // cleared so an unused secret is never kept, and never saved against a
  // service principal or storage account it does not belong to.
  function credentialsForAuthMethod(
    values: BackupAzureSettings
  ): AzureCredentials {
    switch (values.authMethod) {
      case 'servicePrincipalSecret':
        return {
          ...noCredentials,
          tenantId: values.tenantId,
          clientId: values.clientId,
          clientSecret: values.clientSecret,
        };
      case 'servicePrincipalCertificate':
        return {
          ...noCredentials,
          tenantId: values.tenantId,
          clientId: values.clientId,
          clientCertificate: values.clientCertificate,
          clientCertificatePassword: values.clientCertificatePassword,
        };
      case 'managedIdentity':
        return {
          ...noCredentials,
          managedIdentityClientId: values.managedIdentityClientId,
        };
      case 'storageAccountKey':
        return {
          ...noCredentials,
          storageAccountKey: values.storageAccountKey,
        };
      default:
        return noCredentials;
    }
  }

  function handleExport(values: BackupAzureSettings) {
    exportMutate.mutate(buildPayload(values), {
      onSuccess() {
        notifySuccess('Success', 'Backup to Azure Blob Storage completed');
      },
    });
  }

  function handleTest(values: BackupAzureSettings) {
    testMutate.mutate(buildPayload(values), {
      onSuccess() {
        notifySuccess(
          'Success',
          'Successfully connected to Azure Blob Storage'
        );
      },
    });
  }

  async function onSubmit(
    values: BackupAzureSettings,
    { resetForm }: FormikHelpers<BackupAzureSettings>
  ) {
    try {
      await updateMutate.mutateAsync(buildPayload(values));
      notifySuccess('Success', 'Azure Blob backup settings saved successfully');
      // Reset touched state so validation errors don't appear on the saved values
      resetForm({ values });
      onSave?.();
    } catch {
      // error notification handled by the mutation's withError handler
    }
  }
}
